import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, RefreshCw, Ticket, Euro, Calendar, MapPin, Phone, Mail, Clock, Volume2, VolumeX, Users, Edit2, Save, X, Grid, Plus, Trash2, ToggleLeft, ToggleRight, UserPlus, Search, ChevronDown, ChevronUp, Download, ArrowUpDown, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { VENUES } from '../data/showData';
import SeatMap from '../components/SeatMap';

const ADMIN_PASSWORD = 'sistertac2026';

// Capacità posti per ogni teatro
const VENUE_CAPACITIES = {
  'teatro-sant-anna': 197,
  'teatro-incontro': 200,
  'teatro-torretta': 192
};

// Funzione per generare suono di notifica
const playNotificationSound = () => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  // Suono 1 - nota alta
  const oscillator1 = audioContext.createOscillator();
  const gainNode1 = audioContext.createGain();
  oscillator1.connect(gainNode1);
  gainNode1.connect(audioContext.destination);
  oscillator1.frequency.value = 880; // La4
  oscillator1.type = 'sine';
  gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
  oscillator1.start(audioContext.currentTime);
  oscillator1.stop(audioContext.currentTime + 0.3);
  
  // Suono 2 - nota più alta (dopo 0.15s)
  setTimeout(() => {
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();
    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);
    oscillator2.frequency.value = 1108.73; // Do#5
    oscillator2.type = 'sine';
    gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
    oscillator2.start(audioContext.currentTime);
    oscillator2.stop(audioContext.currentTime + 0.4);
  }, 150);
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newBookingAlert, setNewBookingAlert] = useState(false);
  const [capacityData, setCapacityData] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editExternalValue, setEditExternalValue] = useState('');
  const previousBookingsCount = useRef(0);
  const isFirstLoad = useRef(true);
  
  // Seat management state
  const [seatManagementEvent, setSeatManagementEvent] = useState(null);
  const [seatOccupied, setSeatOccupied] = useState([]);  // All occupied (manual + site)
  const [seatBookedViaSite, setSeatBookedViaSite] = useState([]);  // Originally booked via site
  const [seatFreedByAdmin, setSeatFreedByAdmin] = useState([]);  // Seats freed by admin
  const [seatManuallyOccupied, setSeatManuallyOccupied] = useState([]);  // Original manually occupied
  const [seatSelected, setSeatSelected] = useState([]);  // Currently selected for manual occupation
  const [seatToFree, setSeatToFree] = useState([]);  // Site-booked seats to be freed
  const [savingSeats, setSavingSeats] = useState(false);
  
  // Events/dates management state
  const [events, setEvents] = useState([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEventData, setEditingEventData] = useState(null);
  const [newEvent, setNewEvent] = useState({
    venue_id: 'teatro-sant-anna',
    date: '',
    day: '',
    time: '21:00',
    available: true,
    sold_out: false,
    prices: null,
    phone: ''
  });

  // Manual bookings state
  const [manualBookings, setManualBookings] = useState([]);
  const [showManualBookings, setShowManualBookings] = useState(false);
  const [showAddManualBooking, setShowAddManualBooking] = useState(false);
  const [editingManualBooking, setEditingManualBooking] = useState(null);
  const [manualBookingFilter, setManualBookingFilter] = useState({ venue_id: '', date: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'fila', direction: 'asc' });
  const [editingSiteBookingEmail, setEditingSiteBookingEmail] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [changingDateBooking, setChangingDateBooking] = useState(null);
  const [newDateSelection, setNewDateSelection] = useState({ eventId: '', seats: [] });
  const [changeDateOccupiedSeats, setChangeDateOccupiedSeats] = useState([]);
  const [exportDateFilter, setExportDateFilter] = useState('');
  const [bookingsDateFilter, setBookingsDateFilter] = useState('');
  const [bookingsSortBy, setBookingsSortBy] = useState('date'); // 'date', 'seat', 'name'
  
  // WhatsApp Broadcast
  const [showWhatsAppBroadcast, setShowWhatsAppBroadcast] = useState(false);
  const [whatsAppDateFilter, setWhatsAppDateFilter] = useState('');
  const [whatsAppMessage, setWhatsAppMessage] = useState('Ciao {nome}! Ti scriviamo per lo spettacolo SISTER TAC del {data}. ');
  
  // Pinerolo WhatsApp requests
  const [pineroloRequests, setPineroloRequests] = useState([]);
  const [showPineroloSection, setShowPineroloSection] = useState(false);
  const [pineroloStats, setPineroloStats] = useState({ total_requests: 0, total_tickets: 0 });
  
  const [newManualBooking, setNewManualBooking] = useState({
    venue_id: 'teatro-sant-anna',
    date: '14 Marzo 2026',
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    metodo_pagamento: 'Satispay',
    num_biglietti: 1,
    importo: 13,
    fila: 1,
    posti: '',
    note: ''
  });

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchBookings();
      fetchEvents();
    } else {
      setError('Password non corretta');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const createEvent = async () => {
    try {
      const eventData = { ...newEvent };
      if (eventData.prices && (eventData.prices.intero || eventData.prices.ridotto)) {
        eventData.prices = {
          intero: parseInt(eventData.prices.intero) || 15,
          ridotto: parseInt(eventData.prices.ridotto) || 12
        };
      } else {
        eventData.prices = null;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData)
      });
      
      if (response.ok) {
        setShowAddEvent(false);
        setNewEvent({
          venue_id: 'teatro-sant-anna',
          date: '',
          day: '',
          time: '21:00',
          available: true,
          sold_out: false,
          prices: null,
          phone: ''
        });
        fetchEvents();
        fetchBookings();
      }
    } catch (err) {
      console.error('Error creating event:', err);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm('Sei sicuro di voler eliminare questa data?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchEvents();
        fetchBookings();
      }
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const toggleSoldOut = async (eventId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/toggle-soldout`, {
        method: 'POST'
      });
      
      if (response.ok) {
        fetchEvents();
      }
    } catch (err) {
      console.error('Error toggling sold out:', err);
    }
  };

  // ==================== MANUAL BOOKINGS FUNCTIONS ====================
  
  const fetchManualBookings = async () => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-bookings`;
      const params = new URLSearchParams();
      if (manualBookingFilter.venue_id) params.append('venue_id', manualBookingFilter.venue_id);
      if (manualBookingFilter.date) params.append('date', manualBookingFilter.date);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setManualBookings(data.bookings || []);
      }
    } catch (err) {
      console.error('Error fetching manual bookings:', err);
    }
  };

  // Sort manual bookings
  const sortedManualBookings = [...manualBookings].sort((a, b) => {
    const { field, direction } = sortConfig;
    let aVal, bVal;
    
    switch (field) {
      case 'nome':
        aVal = `${a.nome} ${a.cognome}`.toLowerCase();
        bVal = `${b.nome} ${b.cognome}`.toLowerCase();
        break;
      case 'fila':
        aVal = a.fila;
        bVal = b.fila;
        // Se stessa fila, ordina per primo posto
        if (aVal === bVal) {
          const aFirstSeat = parseInt(a.posti.split('-')[0]) || 0;
          const bFirstSeat = parseInt(b.posti.split('-')[0]) || 0;
          return direction === 'asc' ? aFirstSeat - bFirstSeat : bFirstSeat - aFirstSeat;
        }
        break;
      case 'importo':
        aVal = a.importo;
        bVal = b.importo;
        break;
      case 'num_biglietti':
        aVal = a.num_biglietti;
        bVal = b.num_biglietti;
        break;
      case 'metodo_pagamento':
        aVal = a.metodo_pagamento?.toLowerCase() || '';
        bVal = b.metodo_pagamento?.toLowerCase() || '';
        break;
      default:
        aVal = a[field];
        bVal = b[field];
    }
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Toggle sort
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Nome', 'Cognome', 'Telefono', 'Email', 'Metodo Pagamento', 'N. Biglietti', 'Importo', 'Fila', 'Posti', 'Data', 'Teatro'];
    const rows = sortedManualBookings.map(b => [
      b.nome,
      b.cognome,
      b.telefono,
      b.email || '',
      b.metodo_pagamento,
      b.num_biglietti,
      b.importo,
      b.fila,
      b.posti,
      b.date,
      VENUES[b.venue_id]?.name || b.venue_id
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `prenotazioni_${manualBookingFilter.date || 'tutte'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export ALL contacts (site + manual) for marketing
  const exportAllContacts = async (filterDate = null) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/export-contacts`;
      if (filterDate) {
        url += `?date=${encodeURIComponent(filterDate)}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      const headers = ['Nome', 'Cognome', 'Email', 'Telefono', 'Data Evento', 'Biglietti', 'Fonte'];
      const rows = data.contacts.map(c => [
        c.nome,
        c.cognome,
        c.email,
        c.telefono,
        c.data_evento,
        c.biglietti,
        c.fonte
      ]);
      
      const csvContent = [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(';'))
      ].join('\n');
      
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const urlBlob = URL.createObjectURL(blob);
      link.setAttribute('href', urlBlob);
      link.setAttribute('download', `contatti_${filterDate || 'tutti'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`Esportati ${data.unique} contatti unici su ${data.total} totali`);
    } catch (err) {
      console.error('Error exporting contacts:', err);
      alert('Errore durante l\'esportazione');
    }
  };

  // Get contacts with phone for WhatsApp broadcast
  const getWhatsAppContacts = () => {
    const contacts = [];
    
    // From site bookings
    bookings
      .filter(b => !whatsAppDateFilter || b.date === whatsAppDateFilter)
      .forEach(b => {
        if (b.telefono || b.phone) {
          contacts.push({
            nome: `${b.nome || ''} ${b.cognome || ''}`.trim(),
            telefono: b.telefono || b.phone,
            data: b.date,
            fonte: 'sito'
          });
        }
      });
    
    // From manual bookings
    manualBookings
      .filter(b => !whatsAppDateFilter || b.date === whatsAppDateFilter)
      .forEach(b => {
        if (b.phone) {
          contacts.push({
            nome: b.nome || b.name || '',
            telefono: b.phone,
            data: b.date,
            fonte: 'manuale'
          });
        }
      });
    
    // From Pinerolo WhatsApp requests
    if (!whatsAppDateFilter || whatsAppDateFilter.includes('28 Marzo') || whatsAppDateFilter.toLowerCase().includes('pinerolo')) {
      pineroloRequests.forEach(req => {
        if (req.phone) {
          contacts.push({
            nome: req.name || '',
            telefono: req.phone,
            data: '28 Marzo 2026 - Pinerolo',
            fonte: 'pinerolo'
          });
        }
      });
    }
    
    return contacts;
  };

  // Generate WhatsApp link
  const generateWhatsAppLink = (phone, name, date) => {
    let message = whatsAppMessage
      .replace('{nome}', name)
      .replace('{data}', date);
    
    // Clean phone number
    let cleanPhone = phone.replace(/\s+/g, '').replace(/-/g, '');
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '+39' + cleanPhone.substring(1);
    } else if (!cleanPhone.startsWith('+')) {
      cleanPhone = '+39' + cleanPhone;
    }
    
    return `https://wa.me/${cleanPhone.replace('+', '')}?text=${encodeURIComponent(message)}`;
  };

  // Fetch Pinerolo WhatsApp requests
  const fetchPineroloRequests = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whatsapp-requests?venue_id=teatro-incontro`);
      const data = await response.json();
      setPineroloRequests(data.requests || []);
      setPineroloStats({ total_requests: data.total_requests, total_tickets: data.total_tickets });
    } catch (err) {
      console.error('Error fetching Pinerolo requests:', err);
    }
  };

  // Update Pinerolo request status
  const updatePineroloStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whatsapp-requests/${requestId}/status?status=${newStatus}`, {
        method: 'PUT'
      });
      if (response.ok) {
        fetchPineroloRequests();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  // Delete Pinerolo request
  const deletePineroloRequest = async (requestId) => {
    if (!confirm('Eliminare questa richiesta?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whatsapp-requests/${requestId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchPineroloRequests();
      }
    } catch (err) {
      console.error('Error deleting request:', err);
    }
  };

  const createManualBooking = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newManualBooking)
      });
      
      if (response.ok) {
        setShowAddManualBooking(false);
        setNewManualBooking({
          venue_id: 'teatro-sant-anna',
          date: '14 Marzo 2026',
          nome: '',
          cognome: '',
          telefono: '',
          email: '',
          metodo_pagamento: 'Satispay',
          num_biglietti: 1,
          importo: 13,
          fila: 1,
          posti: '',
          note: ''
        });
        fetchManualBookings();
        fetchBookings(); // Refresh capacity data
      }
    } catch (err) {
      console.error('Error creating manual booking:', err);
    }
  };

  const updateManualBooking = async (bookingId, updateData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-bookings/${bookingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        setEditingManualBooking(null);
        fetchManualBookings();
        fetchBookings();
      }
    } catch (err) {
      console.error('Error updating manual booking:', err);
    }
  };

  const deleteManualBooking = async (bookingId) => {
    if (!confirm('Sei sicuro di voler eliminare questa prenotazione? I posti verranno liberati.')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-bookings/${bookingId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchManualBookings();
        fetchBookings();
      }
    } catch (err) {
      console.error('Error deleting manual booking:', err);
    }
  };

  // Fetch manual bookings when section is opened
  useEffect(() => {
    if (showManualBookings && isAuthenticated) {
      fetchManualBookings();
    }
  }, [showManualBookings, manualBookingFilter, isAuthenticated]);

  // Delete site booking
  const deleteSiteBooking = async (bookingId) => {
    if (!confirm('Sei sicuro di voler eliminare questa prenotazione? I posti verranno liberati.')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchBookings();
      }
    } catch (err) {
      console.error('Error deleting site booking:', err);
    }
  };

  // Update email for site booking
  const updateSiteBookingEmail = async (bookingId, newEmail) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail })
      });
      
      if (response.ok) {
        alert('Email aggiornata con successo!');
        fetchBookings();
        return true;
      }
    } catch (err) {
      console.error('Error updating email:', err);
    }
    return false;
  };

  // Resend confirmation email
  const resendBookingEmail = async (bookingId, email) => {
    if (!confirm(`Vuoi reinviare l'email di conferma a ${email}?`)) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/resend-email`, {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('Email inviata con successo!');
      } else {
        const data = await response.json();
        alert(`Errore: ${data.detail || 'Impossibile inviare email'}`);
      }
    } catch (err) {
      console.error('Error resending email:', err);
      alert('Errore nell\'invio dell\'email');
    }
  };

  // Change booking date
  const openChangeDateModal = async (booking) => {
    setChangingDateBooking(booking);
    setNewDateSelection({ eventId: '', seats: [] });
    setChangeDateOccupiedSeats([]);
  };

  const loadOccupiedSeatsForNewDate = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    const venuesWithSeatMap = ['teatro-sant-anna', 'teatro-torretta'];
    if (venuesWithSeatMap.includes(event.venue_id)) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/seats/${event.venue_id}/${encodeURIComponent(event.date)}`
        );
        if (response.ok) {
          const data = await response.json();
          setChangeDateOccupiedSeats([...data.occupied_seats, ...data.blocked_seats]);
        }
      } catch (err) {
        console.error('Error fetching seats for new date:', err);
      }
    }
  };

  const confirmChangeDate = async () => {
    if (!changingDateBooking || !newDateSelection.eventId) {
      alert('Seleziona una nuova data');
      return;
    }

    const newEvent = events.find(e => e.id === newDateSelection.eventId);
    const venuesWithSeatMap = ['teatro-sant-anna', 'teatro-torretta'];
    
    if (venuesWithSeatMap.includes(newEvent?.venue_id)) {
      if (newDateSelection.seats.length !== changingDateBooking.total_tickets) {
        alert(`Seleziona ${changingDateBooking.total_tickets} posto/i dalla piantina`);
        return;
      }
    }

    if (!confirm(`Confermi il cambio data da "${changingDateBooking.date}" a "${newEvent.date}"?\n\nVerrà inviata un'email di conferma al cliente.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${changingDateBooking.id}/change-date`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_event_id: newDateSelection.eventId,
            new_seats: newDateSelection.seats.length > 0 ? newDateSelection.seats : null
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`Cambio data completato!\n\nNuova data: ${result.new_date}\n${result.email_sent ? 'Email inviata al cliente.' : ''}`);
        setChangingDateBooking(null);
        fetchBookings();
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail || 'Impossibile cambiare data'}`);
      }
    } catch (err) {
      console.error('Error changing date:', err);
      alert('Errore nel cambio data');
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const [bookingsRes, summaryRes, capacityRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/summary`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/capacity`)
      ]);
      
      const bookingsData = await bookingsRes.json();
      const summaryData = await summaryRes.json();
      const capacityDataRes = await capacityRes.json();
      
      const newBookings = bookingsData.bookings || [];
      
      // Controlla se ci sono nuove prenotazioni (non al primo caricamento)
      if (!isFirstLoad.current && newBookings.length > previousBookingsCount.current) {
        const numNewBookings = newBookings.length - previousBookingsCount.current;
        if (soundEnabled) {
          playNotificationSound();
        }
        setNewBookingAlert(true);
        setTimeout(() => setNewBookingAlert(false), 5000);
        console.log(`🎫 ${numNewBookings} nuova/e prenotazione/i!`);
      }
      
      previousBookingsCount.current = newBookings.length;
      isFirstLoad.current = false;
      
      setBookings(newBookings);
      setSummary(summaryData);
      setCapacityData(capacityDataRes.events || []);
      setLastUpdate(new Date());
      
      // Also fetch Pinerolo requests
      fetchPineroloRequests();
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateExternalBookings = async (venueId, date, externalBookings) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/capacity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue_id: venueId,
          date: date,
          external_bookings: parseInt(externalBookings) || 0
        })
      });
      
      if (response.ok) {
        setEditingEvent(null);
        fetchBookings(); // Refresh data
      }
    } catch (err) {
      console.error('Error updating external bookings:', err);
    }
  };

  // Build capacity data for all upcoming shows
  const getCapacityForEvent = (venueId, date) => {
    const found = capacityData.find(c => c.venue_id === venueId && c.date === date);
    if (found) return found;
    
    // Default values if not found
    const venue = VENUES[venueId];
    return {
      venue_id: venueId,
      date: date,
      max_capacity: venue?.capacity || 200,
      external_bookings: 0,
      site_bookings: 0,
      total_booked: 0,
      available: venue?.capacity || 200
    };
  };

  // Open seat management for a specific event
  const openSeatManagement = async (show) => {
    setSeatManagementEvent(show);
    setSeatSelected([]);
    setSeatToFree([]);
    setSeatBookedViaSite([]);
    setSeatManuallyOccupied([]);
    setSeatFreedByAdmin([]);
    
    // Fetch current occupied seats
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seats/${show.venueId}/${encodeURIComponent(show.date)}`
      );
      if (response.ok) {
        const data = await response.json();
        const bookedSite = data.original_booked_via_site || data.booked_via_site || [];
        const manuallyOcc = data.manually_occupied || [];
        const blocked = data.blocked_seats || [];
        const freed = data.freed_seats || [];
        
        setSeatBookedViaSite(bookedSite);
        setSeatManuallyOccupied(manuallyOcc);
        setSeatFreedByAdmin(freed);
        setSeatOccupied([...data.booked_via_site, ...manuallyOcc, ...blocked]);
        // Pre-select manually occupied seats for editing
        setSeatSelected([...manuallyOcc]);
        // Pre-set freed seats
        setSeatToFree([...freed]);
      }
    } catch (err) {
      console.error('Error fetching seats:', err);
    }
  };

  // Handle seat selection in admin mode
  const handleAdminSeatSelect = (newSelected) => {
    // Check if any site-booked seat was clicked
    const siteBookedClicked = seatBookedViaSite.filter(s => 
      (newSelected.includes(s) && !seatSelected.includes(s)) ||  // Added a site-booked seat
      (!newSelected.includes(s) && seatSelected.includes(s) && seatBookedViaSite.includes(s))  // Removed a site-booked seat
    );
    
    // Update toFree list for site-booked seats
    const newToFree = [...seatToFree];
    seatBookedViaSite.forEach(seat => {
      const wasInSelected = seatSelected.includes(seat);
      const isNowInSelected = newSelected.includes(seat);
      const isCurrentlyFreed = seatFreedByAdmin.includes(seat);
      
      if (!wasInSelected && isNowInSelected) {
        // Clicked on a site-booked seat that wasn't selected -> mark to restore (remove from toFree)
        const idx = newToFree.indexOf(seat);
        if (idx > -1) newToFree.splice(idx, 1);
      } else if (wasInSelected && !isNowInSelected && !isCurrentlyFreed) {
        // Deselected a site-booked seat -> mark to free
        if (!newToFree.includes(seat)) newToFree.push(seat);
      } else if (!isNowInSelected && isCurrentlyFreed) {
        // Was freed, now being restored
        const idx = newToFree.indexOf(seat);
        if (idx > -1) newToFree.splice(idx, 1);
      }
    });
    
    // For site-booked seats: toggle their free status
    seatBookedViaSite.forEach(seat => {
      if (newSelected.includes(seat) && !seatSelected.includes(seat)) {
        // Was clicked to be freed (deselect from occupied state)
        if (!newToFree.includes(seat) && !seatFreedByAdmin.includes(seat)) {
          newToFree.push(seat);
        } else if (seatFreedByAdmin.includes(seat)) {
          // Was freed, now being restored (re-occupied)
          const idx = newToFree.indexOf(seat);
          if (idx > -1) newToFree.splice(idx, 1);
        }
      }
    });
    
    setSeatToFree(newToFree);
    setSeatSelected(newSelected);
  };

  // Save seat changes - handles both occupying AND freeing seats
  const saveOccupiedSeats = async () => {
    if (!seatManagementEvent) return;
    
    setSavingSeats(true);
    try {
      // 1. Find manually occupied seats to free (were manually occupied, now deselected)
      const manualSeatsToFree = seatManuallyOccupied.filter(s => !seatSelected.includes(s) && !seatBookedViaSite.includes(s));
      
      // 2. Find seats to newly occupy (selected but not in original manual or site-booked)
      const seatsToOccupy = seatSelected.filter(s => 
        !seatManuallyOccupied.includes(s) && !seatBookedViaSite.includes(s)
      );
      
      // 3. Site-booked seats to free (clicked to liberate)
      const siteSeatsToFree = seatToFree.filter(s => !seatFreedByAdmin.includes(s));
      
      // 4. Previously freed seats to restore (re-occupy)
      const seatsToRestore = seatFreedByAdmin.filter(s => !seatToFree.includes(s));
      
      // Execute API calls
      // Free manually occupied seats
      if (manualSeatsToFree.length > 0) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seats/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            venue_id: seatManagementEvent.venueId,
            date: seatManagementEvent.date,
            seats: manualSeatsToFree,
            status: 'available'
          })
        });
      }
      
      // Occupy new seats
      if (seatsToOccupy.length > 0) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seats/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            venue_id: seatManagementEvent.venueId,
            date: seatManagementEvent.date,
            seats: seatsToOccupy,
            status: 'occupied'
          })
        });
      }
      
      // Free site-booked seats - use explicit status to avoid accidental frees
      if (siteSeatsToFree.length > 0) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seats/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            venue_id: seatManagementEvent.venueId,
            date: seatManagementEvent.date,
            seats: siteSeatsToFree,
            status: 'free_site_seat'
          })
        });
      }
      
      // Restore previously freed seats (re-occupy them)
      if (seatsToRestore.length > 0) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seats/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            venue_id: seatManagementEvent.venueId,
            date: seatManagementEvent.date,
            seats: seatsToRestore,
            status: 'occupied'
          })
        });
      }
      
      // Close modal and refresh
      setSeatManagementEvent(null);
      fetchBookings();
    } catch (err) {
      console.error('Error saving seats:', err);
    } finally {
      setSavingSeats(false);
    }
  };

  // Auto-refresh every 30 seconds when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(fetchBookings, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-[#121212] rounded-2xl border border-white/10 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-[#FFD700] mb-2">Admin Dashboard</h1>
              <p className="text-white/60">Inserisci la password per accedere</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl pr-12"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
              
              <Button 
                type="submit"
                className="w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full"
                data-testid="admin-login-btn"
              >
                Accedi
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a] px-4">
      <div className="max-w-6xl mx-auto">
        {/* New Booking Alert Banner */}
        {newBookingAlert && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#25D366] text-white px-6 py-3 rounded-full shadow-lg animate-bounce">
            <span className="font-bold">🎫 Nuova prenotazione ricevuta!</span>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#FFD700]">Dashboard Prenotazioni</h1>
            {lastUpdate && (
              <p className="text-white/50 text-sm mt-1">
                Ultimo aggiornamento: {formatDate(lastUpdate.toISOString())}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setSoundEnabled(!soundEnabled);
                if (!soundEnabled) {
                  // Test sound when enabling
                  playNotificationSound();
                }
              }}
              className={`rounded-full px-4 ${soundEnabled ? 'bg-[#25D366] hover:bg-[#128C7E]' : 'bg-white/10 hover:bg-white/20'}`}
              title={soundEnabled ? 'Suono attivo' : 'Suono disattivato'}
              data-testid="admin-sound-toggle"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            <Button
              onClick={fetchBookings}
              disabled={loading}
              className="bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full px-6"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <div className="flex items-center gap-2">
              <select
                value={exportDateFilter}
                onChange={(e) => setExportDateFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-full px-4 py-2 text-white text-sm"
              >
                <option value="">Tutte le date</option>
                {events.map(event => (
                  <option key={event.id} value={event.date}>
                    {event.date}
                  </option>
                ))}
              </select>
              <Button
                onClick={() => exportAllContacts(exportDateFilter || null)}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-6"
                title={exportDateFilter ? `Esporta contatti per ${exportDateFilter}` : "Esporta tutti i contatti"}
              >
                <Download className="w-4 h-4 mr-2" />
                {exportDateFilter ? 'Esporta Data' : 'Esporta Tutti'}
              </Button>
              <Button
                onClick={() => setShowWhatsAppBroadcast(true)}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-6"
                title="Invia messaggi WhatsApp"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#E91E63]/20 rounded-xl flex items-center justify-center">
                  <Ticket className="w-6 h-6 text-[#E91E63]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Prenotazioni Totali</p>
                  <p className="text-white text-3xl font-bold">{summary.total_bookings}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FFD700]/20 rounded-xl flex items-center justify-center">
                  <Ticket className="w-6 h-6 text-[#FFD700]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Biglietti Venduti</p>
                  <p className="text-white text-3xl font-bold">{summary.total_tickets}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#25D366]/20 rounded-xl flex items-center justify-center">
                  <Euro className="w-6 h-6 text-[#25D366]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Incasso Totale</p>
                  <p className="text-[#25D366] text-3xl font-bold">€{summary.total_revenue?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings by Event */}
        {summary?.by_event && summary.by_event.length > 0 && (
          <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Riepilogo per Evento</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Data</th>
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Location</th>
                    <th className="text-center py-3 px-4 text-white/60 font-medium">Prenotazioni</th>
                    <th className="text-center py-3 px-4 text-white/60 font-medium">Biglietti</th>
                    <th className="text-right py-3 px-4 text-white/60 font-medium">Incasso</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_event.map((event, index) => (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white font-medium">{event.date}</td>
                      <td className="py-3 px-4 text-white/80">{event.venue}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-white font-bold">{event.bookings_count}</span>
                        {event.bookings_count > 0 && (
                          <div className="text-xs text-white/50">
                            {event.site_bookings > 0 && <span className="text-blue-400">{event.site_bookings} sito</span>}
                            {event.site_bookings > 0 && event.manual_bookings > 0 && ' + '}
                            {event.manual_bookings > 0 && <span className="text-orange-400">{event.manual_bookings} man.</span>}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-[#FFD700] font-bold">{event.total_tickets}</span>
                        {event.total_tickets > 0 && (
                          <div className="text-xs text-white/50">
                            {event.site_tickets > 0 && <span className="text-blue-400">{event.site_tickets}</span>}
                            {event.site_tickets > 0 && (event.manual_tickets_from_seatmap > 0 || event.manual_tickets > 0) && ' + '}
                            {(event.manual_tickets_from_seatmap > 0 || event.manual_tickets > 0) && (
                              <span className="text-orange-400">
                                {event.manual_tickets_from_seatmap > 0 ? event.manual_tickets_from_seatmap : event.manual_tickets}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-[#25D366] font-bold">€{event.total_revenue?.toFixed(2)}</span>
                        {event.total_revenue > 0 && (
                          <div className="text-xs text-white/50">
                            {event.site_revenue > 0 && <span className="text-blue-400">€{event.site_revenue?.toFixed(0)}</span>}
                            {event.site_revenue > 0 && event.manual_revenue > 0 && ' + '}
                            {event.manual_revenue > 0 && <span className="text-orange-400">€{event.manual_revenue?.toFixed(0)}</span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex gap-4 text-xs text-white/50">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400"></span> Sito (carta)</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400"></span> Manuali (WhatsApp)</span>
            </div>
          </div>
        )}

        {/* Gestione Date / Eventi */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-[#E91E63]" />
              Gestione Date Spettacoli
            </h2>
            <Button
              onClick={() => setShowAddEvent(true)}
              className="bg-[#E91E63] hover:bg-[#C2185B] rounded-full px-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Data
            </Button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Data</th>
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Teatro</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Orario</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Prezzi</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Stato</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => {
                  const venue = VENUES[event.venue_id];
                  return (
                    <tr key={event.id || index} className={`border-b border-white/5 hover:bg-white/5 ${event.sold_out ? 'opacity-50' : ''}`}>
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">{event.date}</div>
                        <div className="text-white/50 text-xs">{event.day}</div>
                      </td>
                      <td className="py-3 px-4 text-white/80">{venue?.name}, {venue?.city}</td>
                      <td className="py-3 px-4 text-center text-white">{event.time}</td>
                      <td className="py-3 px-4 text-center text-white/80 text-sm">
                        {event.prices ? `€${event.prices.intero}/${event.prices.ridotto}` : '€13'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {event.sold_out ? (
                          <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded-full text-xs">ESAURITO</span>
                        ) : (
                          <span className="bg-[#25D366]/20 text-[#25D366] px-2 py-1 rounded-full text-xs">Attivo</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            onClick={() => toggleSoldOut(event.id)}
                            className={`h-8 w-8 p-0 ${event.sold_out ? 'bg-[#25D366]/20 hover:bg-[#25D366]/30' : 'bg-red-500/20 hover:bg-red-500/30'}`}
                            title={event.sold_out ? 'Riattiva' : 'Segna esaurito'}
                          >
                            {event.sold_out ? <ToggleRight className="w-4 h-4 text-[#25D366]" /> : <ToggleLeft className="w-4 h-4 text-red-400" />}
                          </Button>
                          {['teatro-sant-anna', 'teatro-torretta'].includes(event.venue_id) && (
                            <Button
                              size="sm"
                              onClick={() => openSeatManagement({venueId: event.venue_id, date: event.date})}
                              className="bg-[#E91E63]/20 hover:bg-[#E91E63]/30 h-8 w-8 p-0"
                              title="Gestisci posti"
                            >
                              <Grid className="w-4 h-4 text-[#E91E63]" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => deleteEvent(event.id)}
                            className="bg-red-500/20 hover:bg-red-500/30 h-8 w-8 p-0"
                            title="Elimina data"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {events.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-white/50">
                      Nessuna data configurata. Clicca "Aggiungi Data" per iniziare.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Event Modal */}
        {showAddEvent && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] rounded-2xl border border-white/10 w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Aggiungi Nuova Data</h2>
                <Button
                  onClick={() => setShowAddEvent(false)}
                  className="bg-white/10 hover:bg-white/20 h-8 w-8 p-0 rounded-full"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-white/80">Teatro</Label>
                  <select
                    value={newEvent.venue_id}
                    onChange={(e) => setNewEvent({...newEvent, venue_id: e.target.value})}
                    className="w-full h-10 px-3 bg-[#0a0a0a] border border-white/20 text-white rounded-lg mt-1"
                  >
                    <option value="teatro-sant-anna">Teatro Sant'Anna, Torino</option>
                    <option value="teatro-incontro">Teatro Incontro, Pinerolo</option>
                    <option value="teatro-torretta">Teatro Torretta, Asti</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Data (es. 14 Marzo 2026)</Label>
                    <Input
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                      placeholder="14 Marzo 2026"
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Giorno</Label>
                    <Input
                      value={newEvent.day}
                      onChange={(e) => setNewEvent({...newEvent, day: e.target.value})}
                      placeholder="Sabato"
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white/80">Orario</Label>
                  <Input
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    placeholder="21:00"
                    className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white/80">Telefono specifico (opzionale)</Label>
                  <Input
                    value={newEvent.phone}
                    onChange={(e) => setNewEvent({...newEvent, phone: e.target.value})}
                    placeholder="3480430201"
                    className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                  />
                </div>
                
                <div className="pt-4">
                  <Button
                    onClick={createEvent}
                    className="w-full bg-[#E91E63] hover:bg-[#C2185B] py-3"
                    disabled={!newEvent.date || !newEvent.day}
                  >
                    Aggiungi Data
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gestione Posti / Capacità */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <Users className="w-6 h-6 text-[#E91E63]" />
            Gestione Posti per Data
          </h2>
          <p className="text-white/50 text-sm mb-4">
            Inserisci i posti già prenotati da altri sistemi. I posti dal sito sono calcolati automaticamente.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Data</th>
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Teatro</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Capacità</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Esterni</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Sito</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Totale</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Disponibili</th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {events.filter(e => !e.sold_out).map((show, index) => {
                  const capacity = getCapacityForEvent(show.venue_id, show.date);
                  const isEditing = editingEvent === `${show.venue_id}_${show.date}`;
                  const venue = VENUES[show.venue_id];
                  const availablePercent = (capacity.available / capacity.max_capacity) * 100;
                  const isPinerolo = show.venue_id === 'teatro-incontro';
                  
                  return (
                    <tr key={index} className={`border-b border-white/5 hover:bg-white/5 ${isPinerolo ? 'bg-purple-500/5' : ''}`}>
                      <td className="py-3 px-4 text-white font-medium">
                        {show.date}
                        {isPinerolo && <span className="ml-2 text-purple-400 text-xs">(WhatsApp)</span>}
                      </td>
                      <td className="py-3 px-4 text-white/80">{venue?.name}, {venue?.city}</td>
                      <td className="py-3 px-4 text-center text-white">{capacity.max_capacity}</td>
                      <td className="py-3 px-4 text-center">
                        {isEditing ? (
                          <Input
                            type="number"
                            min="0"
                            value={editExternalValue}
                            onChange={(e) => setEditExternalValue(e.target.value)}
                            className="w-20 h-8 text-center bg-[#0a0a0a] border-white/20 text-white mx-auto"
                            autoFocus
                          />
                        ) : (
                          <span className={isPinerolo ? "text-purple-400" : "text-orange-400"}>{capacity.external_bookings}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-blue-400">{capacity.site_bookings}</td>
                      <td className="py-3 px-4 text-center text-[#FFD700] font-bold">{capacity.total_booked}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-bold ${availablePercent < 20 ? 'text-red-500' : availablePercent < 50 ? 'text-orange-400' : 'text-[#25D366]'}`}>
                          {capacity.available}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex gap-2 justify-center">
                          {['teatro-sant-anna', 'teatro-torretta'].includes(show.venue_id) ? (
                            <Button
                              size="sm"
                              onClick={() => openSeatManagement({venueId: show.venue_id, date: show.date})}
                              className="bg-white/10 hover:bg-white/20 h-8 w-8 p-0"
                              title="Gestisci posti in piantina"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          ) : (
                            <span className="text-white/30 text-xs">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-400"></span> Prenotazioni manuali (WhatsApp)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-400"></span> Prenotazioni dal sito</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-purple-400"></span> Pinerolo (solo WhatsApp)</span>
          </div>
        </div>

        {/* Seat Management Modal */}
        {seatManagementEvent && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] rounded-2xl border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#121212] p-6 border-b border-white/10 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">Gestione Completa Posti</h2>
                  <p className="text-white/60 text-sm">
                    {seatManagementEvent.date} - {VENUES[seatManagementEvent.venueId]?.name}
                  </p>
                </div>
                <Button
                  onClick={() => setSeatManagementEvent(null)}
                  className="bg-white/10 hover:bg-white/20 h-10 w-10 p-0 rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-6">
                {/* Legenda */}
                <div className="flex flex-wrap gap-4 mb-4 p-3 bg-white/5 rounded-lg text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-[#25D366]"></span>
                    <span className="text-white/70">Libero</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-[#FFD700]"></span>
                    <span className="text-white/70">Occupato (manuale)</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-gray-600"></span>
                    <span className="text-white/70">Prenotato via sito</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-[#25D366] ring-2 ring-blue-400"></span>
                    <span className="text-white/70">Liberato dall'admin</span>
                  </span>
                </div>
                
                <p className="text-white/70 text-sm mb-4">
                  <strong>Gestione completa:</strong> Clicca su <strong>qualsiasi</strong> posto per cambiarne lo stato.
                  Puoi occupare posti liberi e <span className="text-red-400 font-semibold">liberare anche quelli prenotati via sito</span> (es. in caso di rimborso).
                </p>
                
                <SeatMap
                  date={seatManagementEvent.date}
                  venueId={seatManagementEvent.venueId}
                  occupiedSeats={seatBookedViaSite.filter(s => !seatToFree.includes(s))}
                  selectedSeats={[...seatSelected, ...seatToFree]}
                  onSeatSelect={(newSelected) => {
                    // Separate site-booked seats from manually managed seats
                    const newToFree = newSelected.filter(s => seatBookedViaSite.includes(s));
                    const newManual = newSelected.filter(s => !seatBookedViaSite.includes(s));
                    setSeatToFree(newToFree);
                    setSeatSelected(newManual);
                  }}
                  maxSeats={VENUE_CAPACITIES[seatManagementEvent.venueId] || 200}
                  isAdminMode={true}
                  freedSeats={seatFreedByAdmin}
                />
                
                {/* Riepilogo modifiche */}
                <div className="mt-4 p-3 bg-white/5 rounded-lg">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-white/70">
                      Occupati manualmente: <span className="text-[#FFD700] font-bold">{seatSelected.filter(s => !seatBookedViaSite.includes(s)).length}</span>
                    </span>
                    <span className="text-white/70">
                      Prenotati via sito: <span className="text-gray-400 font-bold">{seatBookedViaSite.filter(s => !seatToFree.includes(s) && !seatFreedByAdmin.includes(s)).length}</span>
                    </span>
                    {seatToFree.filter(s => !seatFreedByAdmin.includes(s)).length > 0 && (
                      <span className="text-red-400">
                        ⚠️ Da liberare (sito): <span className="font-bold">{seatToFree.filter(s => !seatFreedByAdmin.includes(s)).length}</span>
                      </span>
                    )}
                    {seatFreedByAdmin.filter(s => !seatToFree.includes(s)).length > 0 && (
                      <span className="text-blue-400">
                        Da ripristinare: <span className="font-bold">{seatFreedByAdmin.filter(s => !seatToFree.includes(s)).length}</span>
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Warning for freeing site-booked seats */}
                {seatToFree.filter(s => !seatFreedByAdmin.includes(s)).length > 0 && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm">
                      <strong>⚠️ Attenzione:</strong> Stai per liberare {seatToFree.filter(s => !seatFreedByAdmin.includes(s)).length} posto/i prenotato/i tramite il sito. 
                      Questa azione renderà i posti nuovamente disponibili per la vendita. 
                      Assicurati di aver gestito eventuali rimborsi con il cliente.
                    </p>
                  </div>
                )}
                
                <div className="mt-6 flex gap-4 justify-end">
                  <Button
                    onClick={() => setSeatManagementEvent(null)}
                    className="bg-white/10 hover:bg-white/20 px-6"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={saveOccupiedSeats}
                    disabled={savingSeats}
                    className="bg-[#E91E63] hover:bg-[#C2185B] px-6"
                  >
                    {savingSeats ? 'Salvataggio...' : 'Salva modifiche'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== SEZIONE PINEROLO (WhatsApp) ==================== */}
        <div className="bg-[#121212] rounded-2xl border border-purple-500/30 p-6 mb-8">
          <div 
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setShowPineroloSection(!showPineroloSection)}
          >
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span className="text-2xl">🎭</span>
              <span className="text-purple-400">Pinerolo</span> - Richieste WhatsApp
              <span className="text-white/50 font-normal text-base">
                ({pineroloStats.total_requests} richieste, {pineroloStats.total_tickets} biglietti)
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); fetchPineroloRequests(); }}
                className="bg-purple-500/20 hover:bg-purple-500/30 h-8 w-8 p-0"
                title="Aggiorna"
              >
                <RefreshCw className="w-4 h-4 text-purple-400" />
              </Button>
              {showPineroloSection ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </div>
          </div>
          
          {showPineroloSection && (
            <div className="mt-4">
              <p className="text-white/50 text-sm mb-4">
                Richieste inviate dal form del sito → WhatsApp dell'organizzatore (28 Marzo 2026 - Teatro Incontro)
              </p>
              
              {pineroloRequests.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-4xl">📭</span>
                  <p className="text-white/50 mt-2">Nessuna richiesta ancora</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Nome</th>
                        <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Telefono</th>
                        <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Interi</th>
                        <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Ridotti</th>
                        <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Tot</th>
                        <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Stato</th>
                        <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Data Richiesta</th>
                        <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pineroloRequests.map((req, index) => (
                        <tr key={req.id || index} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-3">
                            <div className="text-white font-medium">{req.nome} {req.cognome}</div>
                            {req.email && <div className="text-white/40 text-xs">{req.email}</div>}
                          </td>
                          <td className="py-3 px-3 text-white/70 text-sm">{req.telefono || '-'}</td>
                          <td className="py-3 px-3 text-center text-white">{req.num_biglietti_interi}</td>
                          <td className="py-3 px-3 text-center text-white/60">{req.num_biglietti_ridotti}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-[#FFD700] font-bold">{req.total_tickets}</span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <select
                              value={req.status}
                              onChange={(e) => updatePineroloStatus(req.id, e.target.value)}
                              className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${
                                req.status === 'confirmed' ? 'bg-[#25D366]/20 text-[#25D366]' :
                                req.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}
                            >
                              <option value="pending">In attesa</option>
                              <option value="confirmed">Confermato</option>
                              <option value="cancelled">Annullato</option>
                            </select>
                          </td>
                          <td className="py-3 px-3 text-white/50 text-xs">
                            {req.created_at ? new Date(req.created_at).toLocaleString('it-IT', { 
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                            }) : '-'}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Button
                              onClick={() => deletePineroloRequest(req.id)}
                              className="bg-red-500/20 hover:bg-red-500/40 h-8 w-8 p-0"
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Riepilogo */}
              {pineroloRequests.length > 0 && (
                <div className="mt-4 p-3 bg-purple-500/10 rounded-lg flex justify-between items-center">
                  <span className="text-purple-400 font-medium">Totale Pinerolo:</span>
                  <div className="flex gap-4">
                    <span className="text-white">{pineroloStats.total_requests} richieste</span>
                    <span className="text-[#FFD700] font-bold">{pineroloStats.total_tickets} biglietti</span>
                    <span className="text-[#25D366]">
                      {pineroloRequests.filter(r => r.status === 'confirmed').length} confermati
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ==================== SEZIONE PRENOTAZIONI MANUALI ==================== */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
          <div 
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setShowManualBookings(!showManualBookings)}
          >
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-[#FFD700]" />
              Prenotazioni Manuali (WhatsApp/Esterne)
              {manualBookings.length > 0 && (
                <span className="text-white/50 font-normal text-base">({manualBookings.length})</span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              {showManualBookings && (
                <Button
                  onClick={(e) => { e.stopPropagation(); setShowAddManualBooking(true); }}
                  className="bg-[#FFD700] hover:bg-[#FFC107] text-black rounded-full px-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi
                </Button>
              )}
              {showManualBookings ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
            </div>
          </div>
          
          {showManualBookings && (
            <div className="mt-4">
              {/* Bottoni filtro rapido */}
              <div className="flex flex-wrap gap-2 mb-3">
                <Button
                  onClick={() => setManualBookingFilter({ venue_id: '', date: '' })}
                  className={`text-xs px-3 py-1 h-7 rounded-full ${!manualBookingFilter.venue_id && !manualBookingFilter.date ? 'bg-[#FFD700] text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                >
                  Tutte
                </Button>
                <Button
                  onClick={() => setManualBookingFilter({ venue_id: 'teatro-incontro', date: '28 Marzo 2026' })}
                  className={`text-xs px-3 py-1 h-7 rounded-full ${manualBookingFilter.venue_id === 'teatro-incontro' ? 'bg-purple-500 text-white' : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'}`}
                >
                  🎭 Pinerolo (28 Marzo)
                </Button>
                {events.filter(e => e.venue_id === 'teatro-sant-anna').map(event => (
                  <Button
                    key={event.date}
                    onClick={() => setManualBookingFilter({ venue_id: 'teatro-sant-anna', date: event.date })}
                    className={`text-xs px-3 py-1 h-7 rounded-full ${manualBookingFilter.date === event.date && manualBookingFilter.venue_id === 'teatro-sant-anna' ? 'bg-[#E91E63] text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                  >
                    {event.date.split(' ')[0]} {event.date.split(' ')[1]}
                  </Button>
                ))}
              </div>
              
              {/* Filtri */}
              <div className="flex flex-wrap gap-4 mb-4 p-3 bg-white/5 rounded-lg">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-white/60 text-xs">Filtra per Teatro</Label>
                  <select
                    value={manualBookingFilter.venue_id}
                    onChange={(e) => setManualBookingFilter({...manualBookingFilter, venue_id: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/20 text-white rounded-md px-3 py-2 mt-1"
                  >
                    <option value="">Tutti i teatri</option>
                    {Object.entries(VENUES).map(([id, venue]) => (
                      <option key={id} value={id}>{venue.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-white/60 text-xs">Filtra per Data</Label>
                  <select
                    value={manualBookingFilter.date}
                    onChange={(e) => setManualBookingFilter({...manualBookingFilter, date: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/20 text-white rounded-md px-3 py-2 mt-1"
                  >
                    <option value="">Tutte le date</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.date}>{event.date} - {VENUES[event.venue_id]?.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={fetchManualBookings}
                    className="bg-white/10 hover:bg-white/20"
                    title="Aggiorna"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={exportToCSV}
                    className="bg-[#25D366] hover:bg-[#128C7E]"
                    title="Esporta CSV"
                    disabled={manualBookings.length === 0}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Lista prenotazioni manuali */}
              {manualBookings.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlus className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50">Nessuna prenotazione manuale trovata</p>
                  <p className="text-white/30 text-sm mt-1">Clicca "Aggiungi" per inserire le prenotazioni WhatsApp</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th 
                          className="text-left py-3 px-3 text-white/60 font-medium text-sm cursor-pointer hover:text-white"
                          onClick={() => handleSort('nome')}
                        >
                          <span className="flex items-center gap-1">
                            Nome
                            {sortConfig.field === 'nome' && (
                              sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                            )}
                          </span>
                        </th>
                        <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Telefono</th>
                        <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Email</th>
                        <th 
                          className="text-center py-3 px-3 text-white/60 font-medium text-sm cursor-pointer hover:text-white"
                          onClick={() => handleSort('metodo_pagamento')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Pagamento
                            {sortConfig.field === 'metodo_pagamento' && (
                              sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                            )}
                          </span>
                        </th>
                        <th 
                          className="text-center py-3 px-3 text-white/60 font-medium text-sm cursor-pointer hover:text-white"
                          onClick={() => handleSort('num_biglietti')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Posti
                            {sortConfig.field === 'num_biglietti' && (
                              sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                            )}
                          </span>
                        </th>
                        <th 
                          className="text-center py-3 px-3 text-white/60 font-medium text-sm cursor-pointer hover:text-white"
                          onClick={() => handleSort('fila')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Fila
                            {sortConfig.field === 'fila' && (
                              sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                            )}
                          </span>
                        </th>
                        <th 
                          className="text-right py-3 px-3 text-white/60 font-medium text-sm cursor-pointer hover:text-white"
                          onClick={() => handleSort('importo')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            €
                            {sortConfig.field === 'importo' && (
                              sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                            )}
                          </span>
                        </th>
                        <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedManualBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-white/5 hover:bg-white/5">
                          {editingManualBooking === booking.id ? (
                            // Editing mode
                            <>
                              <td className="py-2 px-3">
                                <Input
                                  defaultValue={`${booking.nome} ${booking.cognome}`}
                                  onChange={(e) => {
                                    const parts = e.target.value.split(' ');
                                    booking._editNome = parts[0] || '';
                                    booking._editCognome = parts.slice(1).join(' ') || '';
                                  }}
                                  className="bg-[#0a0a0a] border-white/20 text-white text-sm h-8"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  defaultValue={booking.telefono}
                                  onChange={(e) => booking._editTelefono = e.target.value}
                                  className="bg-[#0a0a0a] border-white/20 text-white text-sm h-8"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  defaultValue={booking.email || ''}
                                  placeholder="email@esempio.com"
                                  onChange={(e) => booking._editEmail = e.target.value}
                                  className="bg-[#0a0a0a] border-white/20 text-white text-sm h-8"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <select
                                  defaultValue={booking.metodo_pagamento}
                                  onChange={(e) => booking._editMetodo = e.target.value}
                                  className="bg-[#0a0a0a] border border-white/20 text-white rounded px-2 py-1 text-sm"
                                >
                                  <option value="Bonifico">Bonifico</option>
                                  <option value="Satispay">Satispay</option>
                                  <option value="PayPal">PayPal</option>
                                  <option value="Contanti">Contanti</option>
                                  <option value="Carta">Carta</option>
                                </select>
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  defaultValue={booking.posti}
                                  onChange={(e) => booking._editPosti = e.target.value}
                                  className="bg-[#0a0a0a] border-white/20 text-white text-sm h-8 w-20"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  type="number"
                                  defaultValue={booking.fila}
                                  onChange={(e) => booking._editFila = parseInt(e.target.value)}
                                  className="bg-[#0a0a0a] border-white/20 text-white text-sm h-8 w-16"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  type="number"
                                  defaultValue={booking.importo}
                                  onChange={(e) => booking._editImporto = parseFloat(e.target.value)}
                                  className="bg-[#0a0a0a] border-white/20 text-white text-sm h-8 w-20"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    onClick={() => {
                                      const updateData = {};
                                      if (booking._editNome) updateData.nome = booking._editNome;
                                      if (booking._editCognome) updateData.cognome = booking._editCognome;
                                      if (booking._editTelefono) updateData.telefono = booking._editTelefono;
                                      if (booking._editEmail !== undefined) updateData.email = booking._editEmail;
                                      if (booking._editMetodo) updateData.metodo_pagamento = booking._editMetodo;
                                      if (booking._editPosti) updateData.posti = booking._editPosti;
                                      if (booking._editFila) updateData.fila = booking._editFila;
                                      if (booking._editImporto) updateData.importo = booking._editImporto;
                                      updateManualBooking(booking.id, updateData);
                                    }}
                                    className="bg-[#25D366] hover:bg-[#128C7E] h-8 w-8 p-0"
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => setEditingManualBooking(null)}
                                    className="bg-white/10 hover:bg-white/20 h-8 w-8 p-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            // View mode
                            <>
                              <td className="py-3 px-3">
                                <div className="text-white font-medium">{booking.nome} {booking.cognome}</div>
                                <div className="text-white/40 text-xs">{booking.date}</div>
                              </td>
                              <td className="py-3 px-3 text-white/80 text-sm">{booking.telefono}</td>
                              <td className="py-3 px-3">
                                {booking.email ? (
                                  <span className="text-white/80 text-sm">{booking.email}</span>
                                ) : (
                                  <span className="text-red-400/60 text-xs italic">mancante</span>
                                )}
                              </td>
                              <td className="py-3 px-3 text-center">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  booking.metodo_pagamento === 'Satispay' ? 'bg-red-500/20 text-red-400' :
                                  booking.metodo_pagamento === 'Bonifico' ? 'bg-blue-500/20 text-blue-400' :
                                  booking.metodo_pagamento === 'PayPal' ? 'bg-yellow-500/20 text-yellow-400' :
                                  booking.metodo_pagamento === 'Carta' ? 'bg-green-500/20 text-green-400' :
                                  'bg-white/10 text-white/60'
                                }`}>
                                  {booking.metodo_pagamento}
                                </span>
                              </td>
                              <td className="py-3 px-3 text-center text-[#FFD700] font-bold">{booking.num_biglietti}</td>
                              <td className="py-3 px-3 text-center text-white/80">F{booking.fila} P{booking.posti}</td>
                              <td className="py-3 px-3 text-right text-[#25D366] font-bold">€{booking.importo}</td>
                              <td className="py-3 px-3">
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    onClick={() => setEditingManualBooking(booking.id)}
                                    className="bg-white/10 hover:bg-white/20 h-8 w-8 p-0"
                                    title="Modifica"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => deleteManualBooking(booking.id)}
                                    className="bg-red-500/20 hover:bg-red-500/40 h-8 w-8 p-0"
                                    title="Elimina"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Aggiungi Prenotazione Manuale */}
        {showAddManualBooking && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] rounded-2xl border border-white/10 w-full max-w-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Nuova Prenotazione Manuale</h2>
                <Button
                  onClick={() => setShowAddManualBooking(false)}
                  className="bg-white/10 hover:bg-white/20 h-10 w-10 p-0 rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Teatro</Label>
                    <select
                      value={newManualBooking.venue_id}
                      onChange={(e) => setNewManualBooking({...newManualBooking, venue_id: e.target.value})}
                      className="w-full bg-[#0a0a0a] border border-white/20 text-white rounded-md px-3 py-2 mt-1"
                    >
                      {Object.entries(VENUES).map(([id, venue]) => (
                        <option key={id} value={id}>{venue.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/80">Data</Label>
                    <select
                      value={newManualBooking.date}
                      onChange={(e) => setNewManualBooking({...newManualBooking, date: e.target.value})}
                      className="w-full bg-[#0a0a0a] border border-white/20 text-white rounded-md px-3 py-2 mt-1"
                    >
                      {events.filter(e => e.venue_id === newManualBooking.venue_id).map((event) => (
                        <option key={event.id} value={event.date}>{event.date}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Nome *</Label>
                    <Input
                      value={newManualBooking.nome}
                      onChange={(e) => setNewManualBooking({...newManualBooking, nome: e.target.value})}
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Cognome *</Label>
                    <Input
                      value={newManualBooking.cognome}
                      onChange={(e) => setNewManualBooking({...newManualBooking, cognome: e.target.value})}
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Telefono *</Label>
                    <Input
                      value={newManualBooking.telefono}
                      onChange={(e) => setNewManualBooking({...newManualBooking, telefono: e.target.value})}
                      placeholder="+39 333 123 4567"
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Email</Label>
                    <Input
                      value={newManualBooking.email}
                      onChange={(e) => setNewManualBooking({...newManualBooking, email: e.target.value})}
                      placeholder="email@esempio.com"
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/80">Metodo Pagamento</Label>
                    <select
                      value={newManualBooking.metodo_pagamento}
                      onChange={(e) => setNewManualBooking({...newManualBooking, metodo_pagamento: e.target.value})}
                      className="w-full bg-[#0a0a0a] border border-white/20 text-white rounded-md px-3 py-2 mt-1"
                    >
                      <option value="Satispay">Satispay</option>
                      <option value="Bonifico">Bonifico</option>
                      <option value="PayPal">PayPal</option>
                      <option value="Contanti">Contanti</option>
                      <option value="Carta">Carta</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/80">N. Biglietti</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newManualBooking.num_biglietti}
                      onChange={(e) => setNewManualBooking({...newManualBooking, num_biglietti: parseInt(e.target.value) || 1})}
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Importo €</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newManualBooking.importo}
                      onChange={(e) => setNewManualBooking({...newManualBooking, importo: parseFloat(e.target.value) || 0})}
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Fila *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newManualBooking.fila}
                      onChange={(e) => setNewManualBooking({...newManualBooking, fila: parseInt(e.target.value) || 1})}
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Posti * (es. "1-5" o "3")</Label>
                    <Input
                      value={newManualBooking.posti}
                      onChange={(e) => setNewManualBooking({...newManualBooking, posti: e.target.value})}
                      placeholder="1-5"
                      className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white/80">Note</Label>
                  <Input
                    value={newManualBooking.note}
                    onChange={(e) => setNewManualBooking({...newManualBooking, note: e.target.value})}
                    placeholder="Note aggiuntive..."
                    className="bg-[#0a0a0a] border-white/20 text-white mt-1"
                  />
                </div>
                
                <div className="pt-4">
                  <Button
                    onClick={createManualBooking}
                    className="w-full bg-[#FFD700] hover:bg-[#FFC107] text-black py-3 font-bold"
                    disabled={!newManualBooking.nome || !newManualBooking.cognome || !newManualBooking.telefono || !newManualBooking.posti}
                  >
                    Aggiungi Prenotazione
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings List - Prenotazioni dal sito */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Ticket className="w-6 h-6 text-[#25D366]" />
              Prenotazioni dal Sito (Carta di Credito)
              {bookings.length > 0 && <span className="text-white/50 font-normal text-base">({bookingsDateFilter ? bookings.filter(b => b.date === bookingsDateFilter).length : bookings.length})</span>}
            </h2>
            <div className="flex items-center gap-2">
              <select
                value={bookingsSortBy}
                onChange={(e) => setBookingsSortBy(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="date">Ordina per: Data</option>
                <option value="seat">Ordina per: Fila/Posto</option>
                <option value="name">Ordina per: Nome</option>
              </select>
              <select
                value={bookingsDateFilter}
                onChange={(e) => setBookingsDateFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white text-sm"
              >
                <option value="">Tutte le date</option>
                {events.map(event => (
                  <option key={event.id} value={event.date}>
                    {event.date}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">Nessuna prenotazione dal sito ancora</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Nome</th>
                    <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Telefono</th>
                    <th className="text-left py-3 px-3 text-white/60 font-medium text-sm">Email</th>
                    <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Posti</th>
                    <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Fila/Posto</th>
                    <th className="text-right py-3 px-3 text-white/60 font-medium text-sm">€</th>
                    <th className="text-center py-3 px-3 text-white/60 font-medium text-sm">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings
                    .filter(booking => !bookingsDateFilter || booking.date === bookingsDateFilter)
                    .sort((a, b) => {
                      if (bookingsSortBy === 'seat') {
                        // Sort by first seat (row, then seat number)
                        const getFirstSeat = (booking) => {
                          const seats = booking.seats || [];
                          if (seats.length === 0) return { row: 999, seat: 999 };
                          const [row, seatNum] = seats[0].split('-').map(Number);
                          return { row: row || 999, seat: seatNum || 999 };
                        };
                        const seatA = getFirstSeat(a);
                        const seatB = getFirstSeat(b);
                        if (seatA.row !== seatB.row) return seatA.row - seatB.row;
                        return seatA.seat - seatB.seat;
                      } else if (bookingsSortBy === 'name') {
                        return (a.nome || '').localeCompare(b.nome || '');
                      } else {
                        // Default: sort by date (most recent first)
                        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                      }
                    })
                    .map((booking, index) => {
                    // Parse seats to get fila and posti display
                    const seatsInfo = booking.seats?.map(s => {
                      const [row, seat] = s.split('-');
                      return { row: parseInt(row), seat: parseInt(seat) };
                    }) || [];
                    
                    // Group by row
                    const rowsMap = {};
                    seatsInfo.forEach(s => {
                      if (!rowsMap[s.row]) rowsMap[s.row] = [];
                      rowsMap[s.row].push(s.seat);
                    });
                    
                    // Format display: F1 P1-3, F2 P5
                    const seatsDisplay = Object.entries(rowsMap)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([row, seats]) => {
                        seats.sort((a, b) => a - b);
                        if (seats.length === 1) return `F${row} P${seats[0]}`;
                        return `F${row} P${seats[0]}-${seats[seats.length - 1]}`;
                      })
                      .join(', ');
                    
                    return (
                      <tr key={booking.id || index} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-3">
                          <div className="text-white font-medium">{booking.nome} {booking.cognome}</div>
                          <div className="text-white/50 text-xs">{booking.date}</div>
                        </td>
                        <td className="py-3 px-3 text-white/70 text-sm">{booking.telefono || '-'}</td>
                        <td className="py-3 px-3 text-sm">
                          {editingSiteBookingEmail === booking.id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={editEmailValue}
                                onChange={(e) => setEditEmailValue(e.target.value)}
                                className="bg-[#0a0a0a] border-white/20 text-white text-xs h-7 w-36"
                                placeholder="email@esempio.com"
                              />
                              <Button
                                onClick={async () => {
                                  const success = await updateSiteBookingEmail(booking.id, editEmailValue);
                                  if (success) {
                                    setEditingSiteBookingEmail(null);
                                  }
                                }}
                                className="bg-[#25D366]/20 hover:bg-[#25D366]/40 h-7 w-7 p-0"
                                title="Salva"
                              >
                                <Save className="w-3 h-3 text-[#25D366]" />
                              </Button>
                              <Button
                                onClick={() => setEditingSiteBookingEmail(null)}
                                className="bg-white/10 hover:bg-white/20 h-7 w-7 p-0"
                                title="Annulla"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="text-white/70 truncate max-w-[120px]" title={booking.email}>
                                {booking.email || '-'}
                              </span>
                              <Button
                                onClick={() => {
                                  setEditingSiteBookingEmail(booking.id);
                                  setEditEmailValue(booking.email || '');
                                }}
                                className="bg-white/10 hover:bg-white/20 h-6 w-6 p-0"
                                title="Modifica email"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="text-[#FFD700] font-bold">{booking.total_tickets}</span>
                        </td>
                        <td className="py-3 px-3 text-center text-white/80 text-sm">{seatsDisplay || '-'}</td>
                        <td className="py-3 px-3 text-right">
                          <span className="text-[#25D366] font-bold">€{booking.amount_euros?.toFixed(0)}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button
                              onClick={() => openChangeDateModal(booking)}
                              className="bg-orange-500/20 hover:bg-orange-500/40 h-8 w-8 p-0"
                              title="Cambia data spettacolo"
                            >
                              <Calendar className="w-4 h-4 text-orange-400" />
                            </Button>
                            <Button
                              onClick={() => resendBookingEmail(booking.id, booking.email)}
                              className="bg-blue-500/20 hover:bg-blue-500/40 h-8 w-8 p-0"
                              title="Reinvia email conferma"
                              disabled={!booking.email}
                            >
                              <Mail className="w-4 h-4 text-blue-400" />
                            </Button>
                            <Button
                              onClick={() => deleteSiteBooking(booking.id)}
                              className="bg-red-500/20 hover:bg-red-500/40 h-8 w-8 p-0"
                              title="Elimina prenotazione"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Auto-refresh notice */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-white/30 text-sm">
            La pagina si aggiorna automaticamente ogni 30 secondi
          </p>
          <p className="text-white/50 text-sm flex items-center justify-center gap-2">
            {soundEnabled ? (
              <>
                <Volume2 className="w-4 h-4 text-[#25D366]" />
                <span className="text-[#25D366]">Notifica sonora attiva</span>
              </>
            ) : (
              <>
                <VolumeX className="w-4 h-4" />
                <span>Notifica sonora disattivata</span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Modal Cambio Data */}
      {changingDateBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-orange-400" />
                Cambia Data Prenotazione
              </h3>
              <p className="text-white/60 mt-1">
                Cliente: <span className="text-white font-medium">{changingDateBooking.nome} {changingDateBooking.cognome}</span>
              </p>
              <p className="text-white/60">
                Data attuale: <span className="text-[#E91E63] font-medium">{changingDateBooking.date}</span> - {changingDateBooking.total_tickets} biglietto/i
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-white/80 text-sm block mb-2">Seleziona nuova data:</label>
                <select
                  value={newDateSelection.eventId}
                  onChange={(e) => {
                    setNewDateSelection({ eventId: e.target.value, seats: [] });
                    if (e.target.value) {
                      loadOccupiedSeatsForNewDate(e.target.value);
                    }
                  }}
                  className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                >
                  <option value="">-- Seleziona data --</option>
                  {events
                    .filter(e => !e.sold_out && e.date !== changingDateBooking.date)
                    .map(event => (
                      <option key={event.id} value={event.id}>
                        {event.date} - {VENUES[event.venue_id]?.name || event.venue_id}
                      </option>
                    ))}
                </select>
              </div>

              {/* Seat Map for new date if venue requires it */}
              {newDateSelection.eventId && (() => {
                const selectedEvent = events.find(e => e.id === newDateSelection.eventId);
                const venuesWithSeatMap = ['teatro-sant-anna', 'teatro-torretta'];
                
                if (selectedEvent && venuesWithSeatMap.includes(selectedEvent.venue_id)) {
                  return (
                    <div className="pt-4 border-t border-white/10">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <Grid className="w-5 h-5 text-[#E91E63]" />
                        Seleziona {changingDateBooking.total_tickets} nuovo/i posto/i
                      </h4>
                      <p className="text-white/50 text-sm mb-4">
                        Posti selezionati: {newDateSelection.seats.length} / {changingDateBooking.total_tickets}
                      </p>
                      <SeatMap
                        date={selectedEvent.date}
                        venueId={selectedEvent.venue_id}
                        occupiedSeats={changeDateOccupiedSeats}
                        selectedSeats={newDateSelection.seats}
                        onSeatSelect={(seats) => setNewDateSelection(prev => ({ ...prev, seats }))}
                        maxSeats={changingDateBooking.total_tickets}
                      />
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
              <Button
                onClick={() => setChangingDateBooking(null)}
                className="bg-white/10 hover:bg-white/20 text-white px-6"
              >
                Annulla
              </Button>
              <Button
                onClick={confirmChangeDate}
                disabled={!newDateSelection.eventId}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 disabled:opacity-50"
              >
                Conferma Cambio Data
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal WhatsApp Broadcast */}
      {showWhatsAppBroadcast && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-[#25D366]" />
                Invia WhatsApp agli Spettatori
              </h3>
              <p className="text-white/60 mt-1">
                Seleziona una data e personalizza il messaggio. Clicca su ogni contatto per aprire WhatsApp.
              </p>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Date filter */}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-white/80 text-sm block mb-2">Filtra per data spettacolo:</label>
                  <select
                    value={whatsAppDateFilter}
                    onChange={(e) => setWhatsAppDateFilter(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                  >
                    <option value="">-- Seleziona data --</option>
                    <option value="pinerolo">28 Marzo 2026 - Pinerolo (WhatsApp)</option>
                    {events.map(event => (
                      <option key={event.id} value={event.date}>
                        {event.date} - {VENUES[event.venue_id]?.name || event.venue_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-white/60 text-sm">
                  {whatsAppDateFilter && (
                    <span className="bg-[#25D366]/20 text-[#25D366] px-3 py-2 rounded-full">
                      {getWhatsAppContacts().length} contatti con telefono
                    </span>
                  )}
                </div>
              </div>

              {/* Message template */}
              <div>
                <label className="text-white/80 text-sm block mb-2">
                  Messaggio precompilato (usa {'{nome}'} e {'{data}'} come variabili):
                </label>
                <textarea
                  value={whatsAppMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white min-h-[100px]"
                  placeholder="Ciao {nome}! Ti scriviamo per lo spettacolo del {data}..."
                />
              </div>

              {/* Contacts list */}
              {whatsAppDateFilter && (
                <div className="border border-white/10 rounded-lg overflow-hidden">
                  <div className="bg-white/5 p-3 border-b border-white/10">
                    <h4 className="text-white font-medium">Elenco Contatti - {whatsAppDateFilter}</h4>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {getWhatsAppContacts().length === 0 ? (
                      <div className="p-6 text-center text-white/50">
                        Nessun contatto con numero di telefono per questa data
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-white/5 sticky top-0">
                          <tr>
                            <th className="text-left py-2 px-4 text-white/60 text-sm font-medium">Nome</th>
                            <th className="text-left py-2 px-4 text-white/60 text-sm font-medium">Telefono</th>
                            <th className="text-center py-2 px-4 text-white/60 text-sm font-medium">Fonte</th>
                            <th className="text-center py-2 px-4 text-white/60 text-sm font-medium">Azione</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getWhatsAppContacts().map((contact, index) => (
                            <tr key={index} className="border-t border-white/5 hover:bg-white/5">
                              <td className="py-3 px-4 text-white">{contact.nome || '-'}</td>
                              <td className="py-3 px-4 text-white/80">{contact.telefono}</td>
                              <td className="py-3 px-4 text-center">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  contact.fonte === 'sito' ? 'bg-blue-500/20 text-blue-400' : 
                                  contact.fonte === 'pinerolo' ? 'bg-purple-500/20 text-purple-400' :
                                  'bg-orange-500/20 text-orange-400'
                                }`}>
                                  {contact.fonte}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <a
                                  href={generateWhatsAppLink(contact.telefono, contact.nome, contact.data)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 bg-[#25D366] hover:bg-[#128C7E] text-white px-3 py-1.5 rounded-full text-sm transition-colors"
                                >
                                  <Send className="w-3 h-3" />
                                  Invia
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end">
              <Button
                onClick={() => {
                  setShowWhatsAppBroadcast(false);
                  setWhatsAppDateFilter('');
                }}
                className="bg-white/10 hover:bg-white/20 text-white px-6"
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
